import { useEffect, useRef, useState, useCallback } from "react";
import type { StoreApi } from "zustand";
import i18n from "./i18n/config";
import * as auth from "./auth";
import { fetchWikipediaArticleHtml } from "./wikipediaApi";

export type ConnectionState = "connecting" | "open" | "closed" | "error";

type StoreWithActions = Record<string, unknown>;

type ArticleLoadPayload = {
  article_id?: unknown;
  article?: unknown;
  id?: unknown;
  name?: unknown;
  title?: unknown;
};

function getGameLanguage(store: StoreWithActions): string | null {
  const gameState = store.gameState;
  if (typeof gameState !== "object" || gameState === null) return null;
  const state = gameState as Record<string, unknown>;
  if (typeof state.language === "string") return state.language;
  const challenge = state.challenge;
  if (typeof challenge === "object" && challenge !== null) {
    const language = (challenge as Record<string, unknown>).language;
    if (typeof language === "string") return language;
  }
  return null;
}

function hasAction(
  data: unknown,
): data is { action: string; payload: unknown } {
  return (
    typeof data === "object" &&
    data !== null &&
    "action" in data &&
    "payload" in data &&
    typeof (data as { action: unknown }).action === "string"
  );
}

// soeren: remove bitwise stuff we dont need this many checks worst case is a bad request
// keep it simple preferably only one way how it should work everywhere
function getArticleLoadDetails(
  payload: unknown,
): { articleId: number; title: string } | null {
  if (typeof payload !== "object" || payload === null) return null;

  const articlePayload = payload as ArticleLoadPayload;
  const nestedArticle =
    typeof articlePayload.article === "object" &&
    articlePayload.article !== null
      ? (articlePayload.article as ArticleLoadPayload)
      : null;
  const articleId =
    articlePayload.article_id ??
    articlePayload.id ??
    nestedArticle?.article_id ??
    nestedArticle?.id ??
    articlePayload.article;
  if (
    typeof articleId !== "number" ||
    !Number.isInteger(articleId) ||
    articleId < 0 ||
    articleId > 0xffffffff
  ) {
    return null;
  }

  const title =
    articlePayload.title ??
    articlePayload.name ??
    nestedArticle?.title ??
    nestedArticle?.name;

  return {
    articleId,
    title: typeof title === "string" ? title : "",
  };
}

export type GameSocketReturn = {
  error: string | null;
  send: (message: unknown) => boolean;
};

/**
 * WebSocket hook that routes messages to store methods.
 *
 * Usage:
 * ```ts
 * const { error, send } = useGameSocket(lobbyId, useWikidailyStore)
 * ```
 *
 * Messages like `{action: "update_game", payload: {...}}` route to `store.update_game(payload)`.
 */
export function useGameSocket<TStore extends StoreWithActions>(
  lobbyId: string,
  gameStore: StoreApi<TStore>,
): GameSocketReturn {
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const retryCountRef = useRef(0);
  const connectRef = useRef<() => void>(() => {});
  const articleLoadSequenceRef = useRef(0);

  const connect = useCallback(async () => {
    articleLoadSequenceRef.current += 1;
    // Cleanup any existing connection/timeout before starting a new one
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = undefined;
    }

    if (wsRef.current) {
      // Prevent the old socket from triggering a reconnect
      wsRef.current.onclose = null;
      wsRef.current.close();
    }

    const configuredWsUrl = import.meta.env.VITE_BACKEND_WS_URL?.replace(
      /\/$/,
      "",
    );
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsBaseUrl = configuredWsUrl || `${protocol}//${window.location.host}`;
    const wsUrl = `${wsBaseUrl}/ws/lobby/${lobbyId}`;
    let token: string;
    try {
      token = await auth.getGameplayToken();
    } catch {
      setError(i18n.t("errors.authFailed"));
      return;
    }

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setError(null);
      retryCountRef.current = 0;
      ws.send(JSON.stringify({ action: "auth", payload: { token } }));
    };

    ws.onmessage = async (event: MessageEvent) => {
      let articleLoadSequence: number | null = null;
      try {
        const data = JSON.parse(event.data);
        console.log("[GameSocket] received:", data.action, data);

        if (hasAction(data)) {
          if (data.action === "wiki_validation_failed") {
            setError(i18n.t("errors.wikiValidationFailed"));
            return;
          }
          if (
            data.action === "load_article" ||
            data.action === "render_article"
          ) {
            articleLoadSequence = ++articleLoadSequenceRef.current;
            const details = getArticleLoadDetails(data.payload);
            if (!details) {
              setError(i18n.t("errors.missingArticleId"));
              return;
            }

            const gameLanguage = getGameLanguage(gameStore.getState());
            if (!gameLanguage) {
              setError(i18n.t("errors.articleFetchFailed"));
              return;
            }
            const html = await fetchWikipediaArticleHtml(
              details.articleId,
              gameLanguage,
            );
            if (articleLoadSequence !== articleLoadSequenceRef.current) return;

            const currentStore = gameStore.getState();
            const renderArticle = currentStore.render_article;
            if (typeof renderArticle === "function") {
              renderArticle.call(currentStore, {
                article_id: details.articleId,
                title: details.title,
                html,
              });
              setError(null);
            }
            return;
          }

          const store = gameStore.getState();
          const method = store[data.action];
          if (typeof method === "function") {
            method.call(store, data.payload);
          }
        }
      } catch (err) {
        if (
          articleLoadSequence !== null &&
          articleLoadSequence !== articleLoadSequenceRef.current
        ) {
          return;
        }
        console.error("Failed to handle message:", err);
        setError(
          err instanceof Error
            ? err.message
            : i18n.t("errors.articleFetchFailed"),
        );
      }
    };

    ws.onclose = () => {
      // Only reconnect if this is the current socket and we haven't unmounted (wsRef.current is not null)
      if (ws === wsRef.current) {
        const delay = calculateBackoff(retryCountRef.current);
        console.log(`[GameSocket] Disconnected. Reconnecting in ${delay}ms...`);
        reconnectTimeoutRef.current = setTimeout(() => {
          retryCountRef.current++;
          connectRef.current();
        }, delay);
      }
    };

    ws.onerror = () => {
      setError(i18n.t("errors.connectionFailed"));
    };
  }, [gameStore, lobbyId]);

  useEffect(() => {
    connectRef.current = connect;
    connectRef.current();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  const send = useCallback((message: unknown): boolean => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    }
    setError(i18n.t("errors.websocketClosed"));
    return false;
  }, []);

  return { error, send };
}

export function calculateBackoff(attempt: number): number {
  return Math.min(1000 * Math.pow(2, attempt), 10000);
}

// ==================== TESTS ====================

if (import.meta.vitest) {
  const fc = await import("fast-check");
  const { renderHook, act, waitFor } = await import("@testing-library/react");
  const { describe, it, expect, vi, beforeEach, afterEach } = import.meta
    .vitest;

  describe("calculateBackoff", () => {
    it("calculates exponential delay", () => {
      expect(calculateBackoff(0)).toBe(1000);
      expect(calculateBackoff(1)).toBe(2000);
      expect(calculateBackoff(2)).toBe(4000);
      expect(calculateBackoff(3)).toBe(8000);
    });

    it("caps at maximum delay", () => {
      expect(calculateBackoff(10)).toBe(10000);
      expect(calculateBackoff(100)).toBe(10000);
    });

    it("fuzz: any non-negative integer returns valid delay", () => {
      fc.assert(
        fc.property(fc.integer({ min: 0, max: 10000 }), (attempt) => {
          const delay = calculateBackoff(attempt);
          expect(delay).toBeGreaterThanOrEqual(1000);
          expect(delay).toBeLessThanOrEqual(10000);
        }),
        { numRuns: 100 },
      );
    });
  });

  describe("hasAction", () => {
    it("returns true for valid messages", () => {
      expect(hasAction({ action: "update_game", payload: {} })).toBe(true);
      expect(
        hasAction({
          action: "render_article",
          payload: { html: "<p>test</p>" },
        }),
      ).toBe(true);
    });

    it("returns false for objects without action", () => {
      expect(hasAction({ payload: {} })).toBe(false);
    });

    it("returns false for objects without payload", () => {
      expect(hasAction({ action: "test" })).toBe(false);
    });

    it("returns false for null", () => {
      expect(hasAction(null)).toBe(false);
    });

    it("returns false for primitives", () => {
      expect(hasAction("string")).toBe(false);
      expect(hasAction(123)).toBe(false);
    });

    it("returns false when action is not a string", () => {
      expect(hasAction({ action: 123, payload: {} })).toBe(false);
    });
  });

  describe("useGameSocket", () => {
    type MockWebSocket = {
      readyState: number;
      send: (data: string) => void;
      close: () => void;
      addEventListener: (
        event: string,
        handler: (event: unknown) => void,
      ) => void;
      removeEventListener: (
        event?: string,
        handler?: (event: unknown) => void,
      ) => void;
      onopen: ((event: unknown) => void) | null;
      onclose: ((event: unknown) => void) | null;
      onerror: ((event: unknown) => void) | null;
      onmessage: ((event: { data: string }) => void) | null;
      _trigger: (event: string, data: unknown) => void;
    };

    let mockWsInstance: MockWebSocket;
    let originalWebSocket: typeof WebSocket;

    const createMockStore = () => {
      const storeMethods = {
        gameState: { language: "de" },
        update_game: vi.fn(),
        render_article: vi.fn(),
      };
      return {
        getState: vi.fn(() => storeMethods),
        ...storeMethods,
      };
    };

    beforeEach(() => {
      vi.spyOn(auth, "getGameplayToken").mockResolvedValue("test.jwt.token");

      const listeners: Record<string, Set<(e: unknown) => void>> = {};

      mockWsInstance = {
        readyState: 0, // CONNECTING
        send: vi.fn(),
        close: vi.fn(),
        addEventListener: vi.fn(
          (event: string, handler: (event: unknown) => void) => {
            if (!listeners[event]) listeners[event] = new Set();
            listeners[event].add(handler);
          },
        ),
        removeEventListener: vi.fn(),
        onopen: null,
        onclose: null,
        onerror: null,
        onmessage: null,
        _trigger: (event: string, data: unknown) => {
          if (event === "message" && mockWsInstance.onmessage)
            mockWsInstance.onmessage(data as { data: string });
          if (event === "open" && mockWsInstance.onopen)
            mockWsInstance.onopen(data);
          if (event === "close" && mockWsInstance.onclose)
            mockWsInstance.onclose(data);
          if (event === "error" && mockWsInstance.onerror)
            mockWsInstance.onerror(data);
          listeners[event]?.forEach((h) => h(data));
        },
      };

      originalWebSocket = globalThis.WebSocket;
      function MockSocket() {
        return mockWsInstance as unknown as WebSocket;
      }
      vi.spyOn(globalThis, "WebSocket").mockImplementation(
        MockSocket as unknown as typeof WebSocket,
      );
    });

    afterEach(() => {
      vi.restoreAllMocks();
      globalThis.WebSocket = originalWebSocket;
    });

    it("sets error to null on successful connection", () => {
      const store = createMockStore();
      const { result } = renderHook(() =>
        useGameSocket(
          "lobby-123",
          store as unknown as StoreApi<Record<string, unknown>>,
        ),
      );

      act(() => {
        mockWsInstance._trigger("open", {});
      });

      expect(result.current.error).toBeNull();
    });

    it("returns send function", () => {
      const store = createMockStore();
      const { result } = renderHook(() =>
        useGameSocket(
          "lobby-123",
          store as unknown as StoreApi<Record<string, unknown>>,
        ),
      );

      expect(typeof result.current.send).toBe("function");
    });

    it("send returns false and sets error when WebSocket is not open", () => {
      const store = createMockStore();
      const { result } = renderHook(() =>
        useGameSocket(
          "lobby-123",
          store as unknown as StoreApi<Record<string, unknown>>,
        ),
      );

      mockWsInstance.readyState = WebSocket.CLOSED;

      act(() => {
        const success = result.current.send({ action: "test", payload: {} });
        expect(success).toBe(false);
      });

      expect(result.current.error).toBe("Websocket connection is closed");
    });

    it("ignores messages without valid action", () => {
      const store = createMockStore();
      renderHook(() =>
        useGameSocket(
          "lobby-123",
          store as unknown as StoreApi<Record<string, unknown>>,
        ),
      );

      act(() => {
        mockWsInstance._trigger("message", {
          data: JSON.stringify({ noaction: "here" }),
        });
      });

      expect(store.update_game).not.toHaveBeenCalled();
    });

    it("handles invalid JSON in message gracefully", () => {
      const store = createMockStore();
      const consoleSpy = vi
        .spyOn(console, "error")
        .mockImplementation(() => {});

      renderHook(() =>
        useGameSocket(
          "lobby-123",
          store as unknown as StoreApi<Record<string, unknown>>,
        ),
      );

      expect(() => {
        act(() => {
          mockWsInstance._trigger("message", { data: "invalid json" });
        });
      }).not.toThrow();

      consoleSpy.mockRestore();
    });

    it("loads article HTML through the Wikipedia proxy", async () => {
      const fetchMock = vi.fn().mockResolvedValue({
        ok: true,
        text: vi.fn().mockResolvedValue("<main>Artikel</main>"),
      });
      vi.stubGlobal("fetch", fetchMock);
      const store = createMockStore();

      renderHook(() =>
        useGameSocket(
          "lobby-123",
          store as unknown as StoreApi<Record<string, unknown>>,
        ),
      );

      await waitFor(() => expect(mockWsInstance.onmessage).not.toBeNull());

      act(() => {
        mockWsInstance._trigger("message", {
          data: JSON.stringify({
            action: "load_article",
            payload: { article_id: 42, title: "Douglas Adams" },
          }),
        });
      });

      await waitFor(() => {
        expect(fetchMock).toHaveBeenCalledWith("/wiki/de/42", {
          credentials: "include",
        });
        expect(store.render_article).toHaveBeenCalledWith({
          article_id: 42,
          title: "Douglas Adams",
          html: "<main>Artikel</main>",
        });
      });
    });

    it("reports asynchronous wiki validation failures", async () => {
      const store = createMockStore();
      const { result } = renderHook(() =>
        useGameSocket(
          "lobby-123",
          store as unknown as StoreApi<Record<string, unknown>>,
        ),
      );

      await waitFor(() => expect(mockWsInstance.onmessage).not.toBeNull());
      act(() => {
        mockWsInstance._trigger("message", {
          data: JSON.stringify({
            action: "wiki_validation_failed",
            payload: { article_id: 42, reason: "metadata_mismatch" },
          }),
        });
      });

      await waitFor(() =>
        expect(result.current.error).toBe(
          i18n.t("errors.wikiValidationFailed"),
        ),
      );
    });
  });
}
